/**
 * Connector Service - API calls for connector management
 */

import { createApiClient } from '@shared/auth';
import apiConfig from '../config/apiConfig';
import { extractErrorMessage } from '../utils/responseHelpers';

const client = createApiClient(apiConfig.getConnectorServiceUrl());

/**
 * List available connector types (drives dynamic UI)
 */
export const getConnectorTypes = async () => {
  try {
    const response = await client.get('/connectors/types');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch connector types.') };
  }
};

/**
 * List configured connectors for current org
 */
export const getConnectors = async (params = {}) => {
  try {
    const response = await client.get('/connectors', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch connectors.') };
  }
};

/**
 * Get single connector details
 */
export const getConnector = async (connectorId) => {
  try {
    const response = await client.get(`/connectors/${connectorId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch connector.') };
  }
};

/**
 * Create a new connector
 */
export const createConnector = async (data) => {
  try {
    const response = await client.post('/connectors', data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to create connector.') };
  }
};

/**
 * Update connector configuration
 */
export const updateConnector = async (connectorId, data) => {
  try {
    const response = await client.put(`/connectors/${connectorId}`, data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to update connector.') };
  }
};

/**
 * Delete a connector
 */
export const deleteConnector = async (connectorId) => {
  try {
    await client.delete(`/connectors/${connectorId}`);
    return { success: true };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to delete connector.') };
  }
};

/**
 * Test connection credentials
 */
export const testConnection = async (connectorId) => {
  try {
    const response = await client.post(`/connectors/${connectorId}/test`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to test connection.') };
  }
};

/**
 * Activate connector for polling
 */
export const activateConnector = async (connectorId) => {
  try {
    const response = await client.post(`/connectors/${connectorId}/activate`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to activate connector.') };
  }
};

/**
 * Pause connector polling
 */
export const pauseConnector = async (connectorId) => {
  try {
    const response = await client.post(`/connectors/${connectorId}/pause`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to pause connector.') };
  }
};

/**
 * Trigger manual sync
 */
export const triggerSync = async (connectorId) => {
  try {
    const response = await client.post(`/connectors/${connectorId}/sync`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to trigger sync.') };
  }
};

/**
 * Get sync history for a connector
 */
export const getSyncHistory = async (connectorId, params = {}) => {
  try {
    const response = await client.get(`/connectors/${connectorId}/sync-history`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch sync history.') };
  }
};

/**
 * Get interactions for a connector
 */
export const getInteractions = async (connectorId, params = {}) => {
  try {
    const response = await client.get(`/connectors/${connectorId}/interactions`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch interactions.') };
  }
};

/**
 * Get full interaction detail (with transcript, processing status)
 */
export const getInteractionDetail = async (connectorId, interactionId) => {
  try {
    const response = await client.get(`/connectors/${connectorId}/interactions/${interactionId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch interaction detail.') };
  }
};

/**
 * Retry failed processing for an interaction
 */
export const retryInteractionProcessing = async (connectorId, interactionId) => {
  try {
    const response = await client.post(`/connectors/${connectorId}/interactions/${interactionId}/retry`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to retry processing.') };
  }
};

/**
 * Get audio recording blob for an interaction (authenticated)
 */
export const getInteractionRecording = async (connectorId, interactionId) => {
  try {
    const response = await client.get(
      `/connectors/${connectorId}/interactions/${interactionId}/recording`,
      { responseType: 'blob' },
    );
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch recording.') };
  }
};

/**
 * Upload an audio file to an audio_upload connector for analytics ingestion.
 *
 * @param {number} connectorId
 * @param {FormData} formData  Must contain a `file` entry. Optional form fields:
 *                             occurred_at, agent_identifier, customer_identifier, language.
 * @param {object}   options
 * @param {AbortSignal} [options.signal]     Cancels the in-flight request.
 * @param {function} [options.onProgress]    Called with integer percent (0–100).
 */
export const uploadAudio = async (connectorId, formData, { signal, onProgress } = {}) => {
  try {
    const response = await client.post(
      `/connectors/${connectorId}/upload-audio`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
        onUploadProgress: (e) => {
          if (!onProgress || !e.total) return;
          onProgress(Math.round((e.loaded / e.total) * 100));
        },
      },
    );
    return { success: true, data: response.data };
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      return { success: false, error: 'Upload cancelled.' };
    }
    return { success: false, error: extractErrorMessage(error, 'Failed to upload audio.') };
  }
};
